import { BigImagePickerAndEditor } from '../BigImage/subcomponents'
import {
  Box,
  Checkbox,
  RadioGroup,
  Stack,
  Switch,
  TextArea,
  TextField,
  Typography
} from '@dtx-company/design-system/src'
import { CRM_THIRD_PARTY_ICONS } from '@app/common/src/constants/crmThirdParty'
import { ContactCollectionFormDisplayTypes, FormFields } from './types'
import { DESCRIPTION_MAX } from '../validation'
import { DefaultManager, ManagerProps } from '../components/Manager/Manager'
import { DisplayToggle } from '../components/DisplayToggle'
import { ExternalCollectionCheckboxes } from './subcomponents/ExternalCollectionCheckboxes'
import { FC, ReactNode, useMemo, useState } from 'react'
import { FileUploadInput } from '../../util/SettingsUtil'
import { FlowpageProps } from '../types/FlowpageProps'
import { FormProps, StyledFormV2 } from '../components/Layout'
import { FormTitle } from '../components/Title'
import { ImportContactsOutlined } from '@mui/icons-material'
import { InnerLinkCard } from '../../../components/flowpage/FlowpageLinkCard'
import { LinkInfoFragment } from '@dtx-company/flow-codegen/src/page/generated.types'
import { LinkProvider } from '@dtx-company/inter-app/src/constants/linkTypes'
import { LinkStyleGated } from '../components/LinkStyle/LinkStyleGated'
import { LockedFeatureWrapper } from '../../../../../common/src/components/LockedFeatureWrapper/LockedFeatureWrapper'
import { MaskType, Modal, ModalVariants } from '@dtx-company/shared-components/src'
import { ModalTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { RootPageState } from '@dtx-company/inter-app/src/redux/types'
import { SettingsAndSaveLink } from '../components/Settings'
import { SuccessScreen } from './SuccessScreen'
import { TermsAndConditions } from './TermsAndConditions'
import { Box as _Box } from '@dtx-company/shared-components/src/components/atoms/Box'
import { getCurrentPageGate } from '@app/common/src/utils/getCurrentPageGate'
import { getMultipleGatesErrorMessage } from './utils/getMultipleGatesErrorMessage'
import { getThirdPartyIntegrations } from './utils'
import { getValidatedActionData } from '../typeUtils'
import { sendSuccessNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { shouldProGateThisCRMFeature, useContactCollectionFormState } from './hooks'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCRMFormWrapperStyles } from './hooks/useCRMFormWrapperStyles'
import { useCurrentPage } from '@dtx-company/inter-app/src/redux/slices/utils/currentPage'
import { useFlowpageAnalyticsCollector } from '../../../hooks/useFlowpageAnalyticsCollector'
import { useIsMobile } from '@dtx-company/true-common/src/hooks/useIsMobile'
import { useLockedFeatureAction } from '../../../../../common/src/components/LockedFeatureModal/hooks/useLockedFeatureAction'
import { useModal } from '@dtx-company/inter-app/src/redux/slices/utils/modal'
import { usePageDisplayContext } from '../../../context/page-display'
import { usePageSelector } from '@dtx-company/inter-app/src/hooks/usePageSelector'
import CollectionForm from '../../../components/flowpage/CollectionForm'
import FormControlLabel from '@mui/material/FormControlLabel'
import Image from 'next/image'
import Radio from '@mui/material/Radio'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'
import messages from '@dtx-company/true-common/src/constants/messages'

export const Form: FC<FormProps> = ({ order, handleClose }: FormProps) => {
  const [customTermsToggle, setCustomTermsToggle] = useState('url')
  const page = useCurrentPage()
  const gateLink = getCurrentPageGate(page?.links)
  const gateId = gateLink?.id
  const {
    handleSubmit,
    onSubmit,
    setValue,
    watchAll,
    disabled,
    edit,
    errors,
    setError,
    clearErrors,
    linkTheme,
    setLinkThemeValue
  } = useContactCollectionFormState(order, handleClose)

  const { previewLink: c } = usePageSelector((state: RootPageState) => state.linkEditReducer)
  const curr = c ?? undefined

  const termsPDFUploaded = useMemo(() => watchAll.customTerms?.pdf instanceof File, [watchAll])
  const { hasOperation } = useAuthState()
  const gatedDisabled = shouldProGateThisCRMFeature(hasOperation('view:flowpage_paid_crm'))
  const [showImagePicker, setShowImagePicker] = useState(
    Boolean(watchAll?.thumbNailImgUrl) ?? false
  )
  const openLockedFeatureModal = useLockedFeatureAction({
    lockedFeatureName: 'view:flowpage_paid_crm'
  })

  const pdfv =
    watchAll.customTerms?.pdf instanceof File
      ? watchAll.customTerms.pdf.name
      : (watchAll.customTerms.pdf ?? 'https://www.flowcode.com/terms-of-use')

  return (
    <StyledFormV2 onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        <FormTitle
          icon={<ImportContactsOutlined />}
          title={`${edit ? 'Edit this' : 'Add a'} contact collection form`}
        />

        <TextField
          fullWidth
          placeholder="Title"
          id="link-link-input"
          value={watchAll.title}
          error={Boolean(errors.title)}
          helperText={errors.title?.message ?? ''}
          onChange={e => setValue(FormFields.TITLE, e.target.value, { shouldValidate: true })}
          label="Form Title"
        />

        <TextArea
          value={watchAll.description}
          error={Boolean(errors.description)}
          label="Form Description (optional)"
          onChange={e => setValue(FormFields.DESCRIPTION, e.target.value, { shouldValidate: true })}
          helperText={
            <Stack direction="row">
              {errors?.description?.message ? <Box>{errors.description.message}</Box> : null}
              <Box sx={{ width: '100%', textAlign: 'right !important' }}>
                {`${watchAll.description.length} / ${DESCRIPTION_MAX}`}
              </Box>
            </Stack>
          }
        />

        <Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h4">Information type </Typography>
            <Typography variant="h4">Required</Typography>
          </Stack>
          <Typography>Select at least 1 information type</Typography>
          <Stack direction="row" justifyContent="space-between">
            <Checkbox
              label={'Display name'}
              checked={watchAll.fields.displayName}
              onChange={() => {
                setValue(FormFields.REQUIRED, {
                  ...watchAll.required,
                  displayName: false
                })

                setValue(
                  FormFields.FIELDS,
                  {
                    ...watchAll.fields,
                    displayName: !watchAll.fields.displayName
                  },
                  { shouldValidate: true }
                )
              }}
            />
            <Switch
              label={`is display name required?`}
              labelPlacement="start"
              hiddenLabel
              checked={watchAll.required.displayName}
              disabled={!watchAll.fields.displayName}
              onChange={() =>
                setValue(
                  FormFields.REQUIRED,
                  {
                    ...watchAll.required,
                    displayName: !watchAll.required.displayName
                  },
                  { shouldValidate: true }
                )
              }
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Checkbox
              label={'Email'}
              checked={watchAll.fields.email}
              onChange={() => {
                setValue(FormFields.REQUIRED, {
                  ...watchAll.required,
                  email: false
                })

                setValue(
                  FormFields.FIELDS,
                  {
                    ...watchAll.fields,
                    email: !watchAll.fields.email
                  },
                  { shouldValidate: true }
                )
              }}
            />
            <Switch
              label={`is email required?`}
              labelPlacement="start"
              hiddenLabel
              checked={watchAll.required.email}
              disabled={!watchAll.fields.email}
              onChange={() =>
                setValue(
                  FormFields.REQUIRED,
                  {
                    ...watchAll.required,
                    email: !watchAll.required.email
                  },
                  { shouldValidate: true }
                )
              }
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Checkbox
              label={'Phone'}
              checked={watchAll.fields.phone}
              onChange={() => {
                setValue(FormFields.REQUIRED, {
                  ...watchAll.required,
                  phone: false
                })

                setValue(
                  FormFields.FIELDS,
                  {
                    ...watchAll.fields,
                    phone: !watchAll.fields.phone
                  },
                  { shouldValidate: true }
                )
              }}
            />
            <Switch
              label={`is phone required?`}
              labelPlacement="start"
              hiddenLabel
              checked={watchAll.required.phone}
              disabled={!watchAll.fields.phone}
              onChange={() =>
                setValue(
                  FormFields.REQUIRED,
                  {
                    ...watchAll.required,
                    phone: !watchAll.required.phone
                  },
                  { shouldValidate: true }
                )
              }
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Checkbox
              label={'Address'}
              checked={watchAll.fields.address}
              onChange={() => {
                setValue(FormFields.REQUIRED, {
                  ...watchAll.required,
                  address: false
                })

                setValue(
                  FormFields.FIELDS,
                  {
                    ...watchAll.fields,
                    address: !watchAll.fields.address
                  },
                  { shouldValidate: true }
                )
              }}
            />
            <Switch
              label={`is address required?`}
              labelPlacement="start"
              hiddenLabel
              checked={watchAll.required.address}
              disabled={!watchAll.fields.address}
              onChange={() =>
                setValue(
                  FormFields.REQUIRED,
                  {
                    ...watchAll.required,
                    address: !watchAll.required.address
                  },
                  { shouldValidate: true }
                )
              }
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Checkbox
              label={'Age'}
              checked={watchAll.fields.age}
              onChange={() => {
                setValue(FormFields.REQUIRED, {
                  ...watchAll.required,
                  age: false
                })

                setValue(
                  FormFields.FIELDS,
                  {
                    ...watchAll.fields,
                    age: !watchAll.fields.age
                  },
                  { shouldValidate: true }
                )
              }}
            />
            <Switch
              label={`is age required?`}
              labelPlacement="start"
              hiddenLabel
              checked={watchAll.required.age}
              disabled={!watchAll.fields.age}
              onChange={() =>
                setValue(
                  FormFields.REQUIRED,
                  {
                    ...watchAll.required,
                    age: !watchAll.required.age
                  },
                  { shouldValidate: true }
                )
              }
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Checkbox
              label={'Gender'}
              checked={watchAll.fields.gender}
              onChange={() => {
                setValue(FormFields.REQUIRED, {
                  ...watchAll.required,
                  gender: false
                })

                setValue(
                  FormFields.FIELDS,
                  {
                    ...watchAll.fields,
                    gender: !watchAll.fields.gender
                  },
                  { shouldValidate: true }
                )
              }}
            />
            <Switch
              label={`is gender required?`}
              labelPlacement="start"
              hiddenLabel
              checked={watchAll.required.gender}
              disabled={!watchAll.fields.gender}
              onChange={() =>
                setValue(
                  FormFields.REQUIRED,
                  {
                    ...watchAll.required,
                    gender: !watchAll.required.gender
                  },
                  { shouldValidate: true }
                )
              }
            />
          </Stack>
          <Stack>
            <Stack direction="row" justifyContent="space-between">
              <Checkbox
                label={'Custom input field'}
                checked={watchAll.fields.custom}
                onChange={() => {
                  setValue(FormFields.REQUIRED, {
                    ...watchAll.required,
                    custom: false
                  })

                  setValue(
                    FormFields.FIELDS,
                    {
                      ...watchAll.fields,
                      custom: !watchAll.fields.custom
                    },
                    { shouldValidate: true }
                  )
                }}
              />
              <Switch
                label={`is custom required?`}
                labelPlacement="start"
                hiddenLabel
                checked={watchAll.required.custom}
                disabled={!watchAll.fields.custom}
                onChange={() =>
                  setValue(
                    FormFields.REQUIRED,
                    {
                      ...watchAll.required,
                      custom: !watchAll.required.custom
                    },
                    { shouldValidate: true }
                  )
                }
              />
            </Stack>
            {watchAll.fields.custom && (
              <Stack width="100%">
                <TextField
                  label={'Enter a label or question'}
                  fullWidth
                  value={watchAll.customFieldLabel}
                  error={Boolean(errors.customFieldLabel)}
                  helperText={errors.customFieldLabel ? 'Please add a custom field label' : ''}
                  onChange={e =>
                    setValue(FormFields.CUSTOM_FIELD_LABEL, e.target.value, {
                      shouldValidate: true
                    })
                  }
                />
              </Stack>
            )}
          </Stack>
          <Stack>
            <Stack direction="row" justifyContent="space-between">
              <Checkbox
                label={'Custom input field'}
                checked={watchAll.fields.custom2}
                onChange={() => {
                  setValue(FormFields.REQUIRED, {
                    ...watchAll.required,
                    custom2: false
                  })

                  setValue(
                    FormFields.FIELDS,
                    {
                      ...watchAll.fields,
                      custom2: !watchAll.fields.custom2
                    },
                    { shouldValidate: true }
                  )
                }}
              />
              <Switch
                label={`is custom2 required?`}
                labelPlacement="start"
                hiddenLabel
                checked={watchAll.required.custom2}
                disabled={!watchAll.fields.custom2}
                onChange={() =>
                  setValue(
                    FormFields.REQUIRED,
                    {
                      ...watchAll.required,
                      custom2: !watchAll.required.custom2
                    },
                    { shouldValidate: true }
                  )
                }
              />
            </Stack>

            {watchAll.fields.custom2 && (
              <Stack width="100%">
                <TextField
                  label={'Enter a label or question'}
                  fullWidth
                  value={watchAll.customFieldLabel2}
                  error={Boolean(errors.customFieldLabel2)}
                  helperText={errors.customFieldLabel2 ? 'Please add a custom field label' : ''}
                  onChange={e =>
                    setValue(FormFields.CUSTOM_FIELD_LABEL_2, e.target.value, {
                      shouldValidate: true
                    })
                  }
                />
              </Stack>
            )}
          </Stack>
        </Stack>

        <LockedFeatureWrapper
          featureName="view:flowpage_paid_crm"
          onOpen={events.userClickedGatedCRMTerms}
          sx={{ alignItems: 'flex-start' }}
        >
          <Stack>
            <Typography variant="h4">Custom terms &amp; conditions</Typography>

            <Checkbox
              label="Add a link to your own terms &amp; conditions"
              checked={watchAll.customTerms?.enabled}
              onChange={() =>
                setValue(
                  FormFields.CUSTOM_TERMS,
                  {
                    ...watchAll.customTerms,
                    enabled: !watchAll.customTerms?.enabled
                  },
                  { shouldValidate: true }
                )
              }
              disabled={gatedDisabled}
            />

            {watchAll.customTerms?.enabled && (
              <Stack width="100%" gap={2}>
                <RadioGroup
                  label=""
                  value={customTermsToggle}
                  onChange={e => setCustomTermsToggle(e.target.value)}
                >
                  <FormControlLabel value="url" control={<Radio />} label="Enter a URL" />
                  <FormControlLabel value="pdf" control={<Radio />} label="Upload PDF" />
                </RadioGroup>

                {customTermsToggle === 'url' && (
                  <TextField
                    label="Enter a URL"
                    id="custom-terms-url-input"
                    fullWidth
                    error={pdfv.length === 0}
                    helperText={pdfv.length === 0 ? 'Please enter a valid URL' : ''}
                    value={pdfv}
                    disabled={termsPDFUploaded || gatedDisabled}
                    onChange={e =>
                      setValue(
                        FormFields.CUSTOM_TERMS,
                        {
                          ...watchAll.customTerms,
                          pdf: e.target.value
                        },
                        { shouldValidate: true }
                      )
                    }
                  />
                )}

                {customTermsToggle === 'pdf' && (
                  <FileUploadInput
                    accept="application/pdf"
                    handleUpload={e => {
                      clearErrors(FormFields.CUSTOM_TERMS)

                      if (e) {
                        setValue(
                          FormFields.CUSTOM_TERMS,
                          {
                            ...watchAll.customTerms,
                            pdf: e
                          },
                          { shouldValidate: true }
                        )
                      } else {
                        setValue(FormFields.CUSTOM_TERMS, {
                          ...watchAll.customTerms,
                          pdf: null
                        })
                      }
                    }}
                  />
                )}

                <TextArea
                  disabled={gatedDisabled}
                  value={watchAll.customTerms?.disclaimer ?? ''}
                  label="Terms & Conditions Disclaimer (optional)"
                  error={Boolean(errors.customTerms?.disclaimer)}
                  helperText={
                    <Stack direction="row">
                      {errors.customTerms?.disclaimer ? (
                        <Box>{errors.customTerms?.disclaimer.message ?? ''}</Box>
                      ) : null}
                      <Box sx={{ width: '100%', textAlign: 'right !important' }}>
                        {`${watchAll.customTerms.disclaimer?.length ?? 0} / ${DESCRIPTION_MAX}`}
                      </Box>
                    </Stack>
                  }
                  onChange={e => {
                    setValue(
                      FormFields.CUSTOM_TERMS,
                      {
                        ...watchAll.customTerms,
                        disclaimer: e.target.value
                      },
                      { shouldValidate: true }
                    )
                  }}
                />
              </Stack>
            )}
          </Stack>
        </LockedFeatureWrapper>

        <ExternalCollectionCheckboxes
          watchAll={watchAll}
          setValue={setValue}
          errors={errors}
          setError={setError}
          curr={curr}
        />

        <Stack>
          <Typography variant="h4">Add a custom success message</Typography>

          <Checkbox
            checked={watchAll.successMessage.enabled}
            label="Add a custom message when a visitor successfully submits their contact information"
            onChange={() =>
              setValue(
                FormFields.SUCCESS_MESSAGE,
                {
                  ...watchAll.successMessage,
                  enabled: !watchAll.successMessage.enabled
                },
                { shouldValidate: true }
              )
            }
          />
        </Stack>

        {watchAll.successMessage.enabled && (
          <>
            <TextField
              fullWidth
              label="Message Header"
              placeholder="Enter a header"
              error={Boolean(errors?.successMessage?.header)}
              helperText={errors.successMessage?.header?.message}
              value={watchAll.successMessage.header}
              onChange={e => {
                setValue(
                  `${FormFields.SUCCESS_MESSAGE}`,
                  { ...watchAll.successMessage, header: e.target.value },
                  { shouldValidate: true }
                )
              }}
            />

            <TextArea
              label="Message Body"
              placeholder="Enter body"
              error={Boolean(errors?.successMessage?.body)}
              value={watchAll.successMessage.body ?? ''}
              onChange={e => {
                setValue(
                  `${FormFields.SUCCESS_MESSAGE}`,
                  { ...watchAll.successMessage, body: e.target.value },
                  { shouldValidate: true }
                )
              }}
              helperText={
                <Stack direction="row">
                  {errors.successMessage?.body ? (
                    <Box>{errors.successMessage?.body.message ?? ''}</Box>
                  ) : null}
                  <Box sx={{ width: '100%', textAlign: 'right !important' }}>
                    {`${watchAll.successMessage?.body?.length ?? 0} / ${DESCRIPTION_MAX}`}
                  </Box>
                </Stack>
              }
            />
          </>
        )}

        <Stack>
          <Typography variant="h4">Add an image</Typography>

          <Checkbox
            checked={showImagePicker}
            label="Add an image above your contact collection form"
            inputProps={{
              'aria-label': 'Add an image above your contact collection form'
            }}
            onChange={() => {
              const next = !showImagePicker

              if (next === true) {
                if (watchAll.widgetDisplayType === MaskType.NO_IMAGE) {
                  setValue(FormFields.WIDGET_DISPLAY_TYPE, MaskType.SQUARE)
                }
              } else {
                setValue(FormFields.WIDGET_DISPLAY_TYPE, MaskType.NO_IMAGE)
                setValue(FormFields.IMAGE, '')
              }

              setShowImagePicker(next)
            }}
          />
        </Stack>

        {showImagePicker && (
          <BigImagePickerAndEditor
            imageUrl={watchAll.thumbNailImgUrl ?? ''}
            setValue={setValue}
            imageFormFieldName={FormFields.IMAGE}
            maskTypeFormFieldName={FormFields.WIDGET_DISPLAY_TYPE}
            showImageSizeWarningToast
            id="big-image-picker"
            deleteImage
          />
        )}

        <Stack>
          <DisplayToggle
            displayOptions={[
              {
                title: 'Embedded',
                value: ContactCollectionFormDisplayTypes.EMBED
              },
              { title: 'Link', value: ContactCollectionFormDisplayTypes.DEFAULT },
              {
                title: 'Pop-up',
                value: ContactCollectionFormDisplayTypes.POPUP,
                isLockedFeature: gatedDisabled
              }
            ]}
            displayType={watchAll.displayType}
            width={['auto']}
            onChange={(_e, type) => {
              if (
                type === ContactCollectionFormDisplayTypes.POPUP &&
                !hasOperation('view:flowpage_paid_crm')
              ) {
                openLockedFeatureModal({})
                return
              }
              // If the user selected a Pop-up display type (which makes the CRM form behave as a gate)
              // and the page has another gate that is not the current CRM form (an age gate or
              // another CRM popup) trigger display an error message to the user
              if (
                type === ContactCollectionFormDisplayTypes.POPUP &&
                gateId &&
                gateId !== curr?.id
              ) {
                const gateType = gateLink?.type
                const message = getMultipleGatesErrorMessage(gateType)
                setError(FormFields.DISPLAY_TYPE, { message })
              } else {
                clearErrors(FormFields.DISPLAY_TYPE)
              }

              setValue(FormFields.DISPLAY_TYPE, type)
              if (type !== ContactCollectionFormDisplayTypes.POPUP) {
                setValue(FormFields.FILLED_OUT_FORM_REQ, false)
              }
            }}
          />
        </Stack>

        {watchAll.displayType === ContactCollectionFormDisplayTypes.POPUP && (
          <Stack width="100%">
            <Switch
              checked={watchAll.filledOutFormRequirement}
              onChange={() =>
                setValue(FormFields.FILLED_OUT_FORM_REQ, !watchAll.filledOutFormRequirement)
              }
              label="All visitors must fill out required inputs before being able to view your page"
            />
          </Stack>
        )}

        <Switch
          checked={watchAll.disableConfetti}
          onChange={() => setValue(FormFields.DISABLE_CONFETTI, !watchAll.disableConfetti)}
          label="Disable submission confetti"
        />

        <LinkStyleGated
          hiddenFields={{
            shadowColor: true,
            borderColor: watchAll.displayType === ContactCollectionFormDisplayTypes.EMBED
          }}
          linkTheme={linkTheme}
          setLinkTheme={setLinkThemeValue}
        />

        <SettingsAndSaveLink disabled={disabled} curr={curr} handleClose={handleClose} />
      </Stack>
    </StyledFormV2>
  )
}

/**
 * Wrapper which provides color, backgroundColor and other styles for the CRM form
 * link when rendered on a flowpage
 */
const CRMFormStyles = ({
  children,
  linkTheme,
  crmDisplayType
}: {
  children: ReactNode
  linkTheme: LinkInfoFragment['linkTheme']
  crmDisplayType: LinkInfoFragment['displayType']
}): JSX.Element => {
  const crmFormStyles = useCRMFormWrapperStyles({ linkTheme, crmDisplayType })

  return (
    <_Box {...crmFormStyles} width="100%" display="block" px="21px" py="30px">
      {children}
    </_Box>
  )
}

export const Flowpage: FC<FlowpageProps> = ({
  link,
  preview = false,
  editLinkMode,
  isEditLinkPreview
}: FlowpageProps) => {
  const {
    page: { slugName }
  } = usePageDisplayContext()
  const { setIsGateCRMFormSubmitted } = usePageDisplayContext()
  const { modalOpen, setClose, setOpen, props } = useModal(ModalTypes.COLLECTION)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const { trackLinkClick, trackCRMWidgetAction } = useFlowpageAnalyticsCollector({
    isPreview: preview,
    isEditMode: Boolean(editLinkMode || isEditLinkPreview)
  })

  const mobile = useIsMobile()
  const actionData = getValidatedActionData<'contactCollection'>(link.actionData, 'fields')
  const isEmbeddedForm = ContactCollectionFormDisplayTypes.EMBED === link.displayType

  const handleSubmissionSuccess = (): void => {
    if (actionData?.successMessage?.enabled) {
      setShowSuccessMessage(true)
    } else {
      sendSuccessNotification(messages.collection.success)
    }

    if (link.displayType === ContactCollectionFormDisplayTypes.POPUP) {
      setIsGateCRMFormSubmitted(true)
    }

    // Default form appears in a modal. Automatically close the modal if no custom message
    // has been specified on successful submission
    if (link.displayType === ContactCollectionFormDisplayTypes.DEFAULT) {
      if (!actionData?.successMessage?.enabled) {
        setClose()
      }
    }
  }

  return link.displayType === ContactCollectionFormDisplayTypes.DEFAULT ? (
    <>
      <Box
        id={link.id}
        sx={{ width: '100%' }}
        onClick={() => {
          trackLinkClick({ link_id: link.id }, { useHeap: true })
          trackCRMWidgetAction({
            link_id: link.id,
            actionType: 'CRM_WIDGET_ACTION_TYPE_CLICK_TO_OPEN_MODAL',
            displayType: link.displayType
          })

          if (!preview && !editLinkMode) {
            const widgetObj = {
              provider: LinkProvider.WIDGET,
              type: 'contactCollection',
              id: 'contactCollection'
            }

            setOpen({ widgetObj: widgetObj, curr: link })
          }
        }}
      >
        <InnerLinkCard
          link={link}
          editLinkMode={editLinkMode}
          iconSrc={link.thumbNailImgUrl ?? undefined}
          preview={preview}
        />
      </Box>
      <Modal
        open={modalOpen && props?.curr?.id === link.id}
        disableAutoClose={!showSuccessMessage}
        onClose={() => {
          trackCRMWidgetAction({
            link_id: link.id,
            actionType: 'CRM_WIDGET_ACTION_TYPE_CLICK_TO_DISMISS_MODAL',
            displayType: link.displayType
          })
          setClose()
        }}
        variant={mobile ? ModalVariants.SHEET : ModalVariants.DESKTOP}
        wrapperProps={{ width: '444px', minHeight: 'min-content' }}
        top="unset"
        zIndex={10001}
      >
        <CRMFormStyles crmDisplayType={link.displayType} linkTheme={link.linkTheme}>
          {showSuccessMessage ? (
            <SuccessScreen
              successMessageObj={actionData?.successMessage}
              slugName={slugName}
              fontColor="inherit"
            />
          ) : (
            <CollectionForm
              handleSubmissionSuccess={handleSubmissionSuccess}
              link={props?.curr ?? undefined}
            />
          )}
          <TermsAndConditions
            customTermsObj={actionData?.customTerms}
            customTermsUrl={actionData?.link}
          />
        </CRMFormStyles>
      </Modal>
    </>
  ) : (
    <CRMFormStyles crmDisplayType={link.displayType} linkTheme={link.linkTheme}>
      {showSuccessMessage ? (
        <SuccessScreen
          successMessageObj={actionData?.successMessage}
          slugName={slugName}
          fontColor="inherit"
        />
      ) : (
        <CollectionForm
          embedded={isEmbeddedForm}
          link={link}
          handleSubmissionSuccess={handleSubmissionSuccess}
        />
      )}
      <TermsAndConditions
        customTermsObj={actionData?.customTerms}
        customTermsUrl={actionData?.link}
      />
    </CRMFormStyles>
  )
}

export const Manager: FC<ManagerProps> = props => {
  const integrations = getThirdPartyIntegrations(props.link.actionData)

  return (
    <DefaultManager {...props} iconUrl={'/icons/product/crm.svg'}>
      {integrations?.length > 0 && (
        <Stack sx={{ width: '100%', px: 4, pb: 4 }} direction="row" gap={2} alignItems="center">
          <Typography variant="h5">Integrations:</Typography>

          {integrations.map(integration => {
            const src = integration !== null ? CRM_THIRD_PARTY_ICONS[integration] : undefined
            return src ? (
              <Image key={`integration ${integration}`} alt="" height={24} width={24} src={src} />
            ) : null
          })}
        </Stack>
      )}
    </DefaultManager>
  )
}
